import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import { collection, addDoc, doc, getDocs, updateDoc, deleteDoc, query, where, Timestamp } from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import PromoteClassesModal from './PromoteClassesModal';
import SuccessModal from './SuccessModal';
import '../styles/Classes.css'

Modal.setAppElement('#root');

const Classes = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [newClass, setNewClass] = useState({ name: '', section: '', session: '', term: '', formMaster: 'N/A' });
  const [editingClass, setEditingClass] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFetchingClasses, setIsFetchingClasses] = useState(true);
  const [currentClass, setCurrentClass] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPromoteClassesModalOpen, setIsPromoteClassesModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const classesCollectionRef = collection(db, "classes");
  const sectionsCollectionRef = collection(db, "sections");
  const sessionsCollectionRef = collection(db, "sessions");
  const termsCollectionRef = collection(db, "terms");


  useEffect(() => {
    fetchClasses();
    fetchSections();
    fetchSessions();
    fetchTerms();
    fetchTeachers();
  }, []);

  const fetchClasses = async () => {
    setIsFetchingClasses(true);
    try {
      // Fetch all reference data collections first
      const [sectionsSnap, sessionsSnap, termsSnap] = await Promise.all([
        getDocs(collection(db, "sections")),
        getDocs(collection(db, "sessions")),
        getDocs(collection(db, "terms")),
      ]);

      const formMastersQuery = query(
        collection(db, "users"),
        where("status", "in", ["Teacher", "Cashier", "Admin"]),
        where("isActive", "==", true)
      );
      
      const formMastersSnap = await getDocs(formMastersQuery);

      // Convert each snapshot to an id => name map
      const sectionsMap = {};
      sectionsSnap.forEach(doc => sectionsMap[doc.id] = doc.data().name);

      const sessionsMap = {};
      sessionsSnap.forEach(doc => sessionsMap[doc.id] = doc.data().name);

      const termsMap = {};
      termsSnap.forEach(doc => termsMap[doc.id] = doc.data().name);

      const formMastersMap = {};
      formMastersSnap.forEach(doc => {
        // Assuming user doc has fields 'firstName' and 'lastName' or 'name'
        const userData = doc.data();
        const name = userData.name || `${userData.firstName} ${userData.lastName}` || "Unknown Teacher";
        formMastersMap[doc.id] = name;
      });

      // Fetch classes
      const classesSnap = await getDocs(classesCollectionRef);

      const classesWithNames = classesSnap.docs.map(doc => {
        const data = doc.data();
      
        return {
          id: doc.id,
          ...data,
          section: sectionsMap[data.section] || "Unknown section",
          sectionId: data.section,
          session: sessionsMap[data.session] || "Unknown session",
          sessionId: data.session,
          term: termsMap[data.term] || "Unknown term",
          termId: data.term,
          formMaster: formMastersMap[data.formMaster] || "N/A",
          formMasterId: data.formMaster,
        };
      });      

      setClasses(classesWithNames);
    } catch (error) {
      console.error("Error fetching classes or related data:", error);
      setError("Failed to fetch classes");
      setShowModal(true);
    } finally {
      setIsFetchingClasses(false);
    }
  };

  const fetchSections = async () => {
    try {
      const data = await getDocs(sectionsCollectionRef);
      setSections(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch sections");
      setShowModal(true);
    }
  };

  const fetchSessions = async () => {
    try {
      const data = await getDocs(sessionsCollectionRef);
      setSessions(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch sessions");
      setShowModal(true);
    }
  };

  const fetchTerms = async () => {
    try {
      const data = await getDocs(termsCollectionRef);
      setTerms(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch terms");
      setShowModal(true);
    }
  };

  const fetchTeachers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "users"));
      const teacherList = usersSnapshot.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter(user => ['Teacher', 'Cashier', 'Admin'].includes(user.status) && user.isActive === true);
      setTeachers(teacherList);
    } catch (error) {
      setError("Failed to fetch teachers");
      setShowModal(true);
    }
  };  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClass({ ...newClass, [name]: value });
  };

  const handleAddClass = async () => {   
    setError("");
    setSuccessMessage("") ;

    const requiredFields = {
      name: "Class name",
      section: "Section",
      session: "Session",
      term: "Term",
    };
    for (const [field, displayName] of Object.entries(requiredFields)) {
      const value = field === "name" ? newClass[field].trim() : newClass[field];
      if (!value) {
        setError(`${displayName} cannot be empty`);
        setShowModal(true);
        return;
      }
    }  
    setUpdate(true);
    setError("");
    setSuccessMessage("");
    try {
      await addDoc(classesCollectionRef, {
        ...newClass,
        formMaster: newClass.formMaster || "No Class Teacher",
        subjects: [],
        students: [], 
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Class added successfully");
      fetchClasses();
      setNewClass({ name: '', section: '', session: '', term: '' });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add class");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateClass = async () => {
    setError("");
    setSuccessMessage("") ;
    setUpdate(true);
    try {
      const classDoc = doc(db, "classes", editingClass.id);
  
      // Prepare updated class data with formMaster defaulting to "N/A"
      const updatedClass = {
        ...newClass,
        formMaster: newClass.formMaster && newClass.formMaster !== "" ? newClass.formMaster : "No Class Teacher",
      };
  
      await updateDoc(classDoc, updatedClass);
  
      setSuccessMessage("Class updated successfully");
      fetchClasses();
      setEditingClass(null);
      setNewClass({ name: '', section: '', session: '', term: '', formMaster: '' });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update class");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };
  

  const handleEditClass = (classToEdit) => {
    setError("");
    setSuccessMessage("") ;
    setEditingClass(classToEdit);   
    setNewClass({
      name: classToEdit.name,
      section: classToEdit.sectionId,
      session: classToEdit.sessionId,
      term: classToEdit.termId,
      formMaster: classToEdit.formMasterId || 'No Class Teacher'
    });
    
    
    setIsEditModalOpen(true);
  };

  const handleDeleteClass = async (id) => {
    setError("");
    setSuccessMessage("") ;
    setUpdate(true);
    try {
      const classDoc = doc(db, "classes", id);
      await deleteDoc(classDoc);
      setSuccessMessage("Class deleted successfully");
      fetchClasses();
    } catch (error) {
      setError("Failed to delete class");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  const openDeleteModal = (classItem) => {
    setCurrentClass(classItem);
    setIsDeleteModalOpen(true);
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <Loader text={"READING"} />;
  }

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}
      {allowedRoles.includes(userData.status) ? (
        <>
      <div className="top-elements row align-center space-between">
        <Link to="/home" className="back-link">
          <i className="fa-solid fa-arrow-left size3"></i>
        </Link>
        <div className="column align-center justify-center">
          <img src={Logo} className="middle-logo" alt="logo" />
        </div>
        <Link to="#" className="home-link" onClick={() => fetchClasses()}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>
      <div className="login-page-items column align-center scroll-fixed">
        <h2 className="manage-page-heading caps size4">Manage Classes</h2>
        <div className="row align-center justify-center gap-15" style={{width: '100%', maxWidth: '600px'}}>
          <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Class</button>
          <button className="login-button login-button-d caps" onClick={() => setIsPromoteClassesModalOpen(true)}>Promote Classes</button>                 
        </div>
          <input
            type="text"
            placeholder="Search by class name, section, session or term"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="input-field1"
          />
          <div className="scroll-fixed scroll-fixed1">        
            <div className="class-list class-list-d scrollable">
              {isFetchingClasses ? (
                <p className="male-female-stat">Loading classes...</p>
              ) : classes.length === 0 ? (
                <p className="male-female-stat">No classes available</p>
              ) : (
                classes
                .filter((item) => {
                  if (!searchText.trim()) return true; // Show all if no search text

                  const queryLower = searchText.toLowerCase().trim();
                  const searchTerms = queryLower.split(/\s+/).filter(term => term.length > 0);

                  const searchableText = [
                    item.name || '',
                    item.section || '',
                    item.session || '',
                    item.term || '',
                    item.formMaster || ''
                  ].join(' ').toLowerCase();

                  return searchTerms.every(term => searchableText.includes(term));
                })
                .map((classItem) => (
                  <div key={classItem.id} className="class-item">
                    <div className="column justify-center">
                      <p>{classItem.name} - {classItem.section}</p>
                      <p className="class-item-small-text">
                        {classItem.session} - {classItem.term} term <br />
                        <span>{classItem.formMaster}</span>
                      </p>                  
                    </div>
                    <div className="row align-center justify-center gap-15">
                      <Link to={`/classes/${classItem.id}`} className="list-btn">
                        <i className="fas fa-eye"></i>
                      </Link>
                      <button className="list-btn" onClick={() => handleEditClass(classItem)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="list-btn" onClick={() => openDeleteModal(classItem)}>
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
      </div>
      </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Class Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Class"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Class</h2>
        <form className="class-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Class Name"
            value={newClass.name}
            onChange={handleInputChange}
          />
          <select
            name="section"
            className="input-field"
            value={newClass.section}
            onChange={handleInputChange}
          >
            <option value="" disabled>Select Section</option>
            {sections.map((section) => (
              <option key={section.id} value={section.id}>{section.name}</option>
            ))}
          </select>
          <select
            name="session"
            className="input-field"
            value={newClass.session}
            onChange={handleInputChange}
          >
            <option value="" disabled>Select Session</option>
            {sessions.map((session) => (
              <option key={session.id} value={session.id}>{session.name}</option>
            ))}
          </select>

          <select
            name="term"
            className="input-field"
            value={newClass.term}
            onChange={handleInputChange}
          >
            <option value="" disabled>Select Term</option>
            {terms.map((term) => (
              <option key={term.id} value={term.id}>{term.name}</option>
            ))}
          </select>
          <select
            name="formMaster"
            className="input-field"
            value={newClass.formMaster}
            onChange={handleInputChange}
          >
            <option value="N/A">Select Form Master (optional)</option>
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.firstName} {teacher.lastName}
              </option>
            ))}
          </select>

          <div className="row align-center gap-1">            
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddClass}>Add Class</button>
          </div>
        </form>
      </Modal>

      {/* Edit Class Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewClass({ name: '', section: '', session: '', term: '', formMaster: 'N/A' }); // Reset newClass
          setEditingClass(null); // Reset editingClass
        }}
        contentLabel="Edit Class"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Class</h2>
        <form className="class-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Class Name"
            value={newClass.name}
            onChange={handleInputChange}
          />
          <select
            name="section"
            className="input-field"
            value={newClass.section}
            onChange={handleInputChange}
          >
            <option value="" disabled>
              Select Section
            </option> 
            {sections.map((section) => ( 
              <option key={section.id} value={section.id}>{section.name}</option>
            ))} 
          </select>
          <select
            name="session"
            className="input-field"
            value={newClass.session}
            onChange={handleInputChange}
          >
            <option value="" disabled>Select Session</option>
            {sessions.map((session) => (
              <option key={session.id} value={session.id}>{session.name}</option>
            ))}
          </select>

          <select
            name="term"
            className="input-field"
            value={newClass.term}
            onChange={handleInputChange}
          >
            <option value="" disabled>Select Term</option>
            {terms.map((term) => (
              <option key={term.id} value={term.id}>{term.name}</option>
            ))}            
          </select>
          <select
            name="formMaster"
            className="input-field"
            value={newClass.formMaster}
            onChange={handleInputChange}
          >
            <option value="N/A">Select Form Master (optional)</option>
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.firstName} {teacher.lastName}
              </option>
            ))}
          </select>
          <div className="row align-center gap-1"> 
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setNewClass({ name: '', section: '', session: '', term: '', formMaster: 'N/A' }); // Reset newClass
                setEditingClass(null); // Reset editingClass
              }}>Cancel</button> 
            <button className="login-button" type="button" onClick={handleUpdateClass}>Update Class</button> 
          </div> 
        </form> 
      </Modal> 

      {/* Promote Classes Modal */}
      <PromoteClassesModal
        isOpen={isPromoteClassesModalOpen}
        onClose={() => setIsPromoteClassesModalOpen(false)}
        classes={classes}
        sessions={sessions}
        terms={terms}
        onSuccess={fetchClasses}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Class"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Class</h2>
        {currentClass && (
          <div>
            <p className="male-female-stat">Are you sure you want to delete <strong>{currentClass.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button" type="button" onClick={() => handleDeleteClass(currentClass.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>
    </> ); 
  };
export default Classes;